import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCurrenciesData, CALCULATION_CURRENCY } from "../utils/currencyUtils";
import { CurrencyData } from "../global/types";
import { useSelector } from "react-redux";
import { selectSettings } from "../store/authSlice";
import { useState, useEffect } from "react";
import { resolveReportingCurrency } from "../utils/reportingCurrency";

type UseCurrencyResult = {
    currencies: CurrencyData[];
    currency: CurrencyData | undefined;
    baseCurrency: CurrencyData | undefined;
    formatMoney: (amount: number, useBaseCurrency?: boolean, compact?: boolean) => string;
    amountRender: (accessor: string) => (item: any) => JSX.Element;
};

export default function useCurrency(): UseCurrencyResult {
    const { i18n } = useTranslation();
    const settings = useSelector(selectSettings);
    const language = i18n.language || navigator.language;
    const reportingCurrency = resolveReportingCurrency(settings ?? {});
    const userCurrencyCode = 'code' in reportingCurrency ? reportingCurrency.code : CALCULATION_CURRENCY;
    const baseCurrencyCode = userCurrencyCode;

    const [currencies, setCurrencies] = useState<CurrencyData[]>([]);

    useEffect(() => {
        let isMounted = true;
        getCurrenciesData(language, baseCurrencyCode, userCurrencyCode).then(data => {
            if (isMounted) setCurrencies(data);
        });
        return () => { isMounted = false; };
    }, [language, baseCurrencyCode, userCurrencyCode]);

    const currency = useMemo(
        () => currencies.find(c => c.code === userCurrencyCode) || currencies[0],
        [currencies, userCurrencyCode]
    );

    const baseCurrency = currency;

    const formatMoney = useCallback(
        (amount: number, useBaseCurrency: boolean = true, compact: boolean = false) => {
            const formatter = (value: number, currencyObj: CurrencyData, isCompact: boolean) => {
                if (isCompact) {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: currencyObj.code,
                        currencyDisplay: 'symbol',
                        notation: 'compact',
                        maximumFractionDigits: 1
                    }).format(value);
                }
                return currencyObj.formatMoney(value, false);
            };

            if (useBaseCurrency && baseCurrency) {
                return formatter(amount, baseCurrency, compact);
            } else if (currency) {
                return formatter(amount, currency, compact);
            }
            // Fallback
            if (compact) {
                return new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    maximumFractionDigits: 1
                }).format(amount);
            }
            return amount.toFixed(2);
        },
        [currency, baseCurrency]
    );

    const amountRender = (accessor: string) => (item: any) => {
        const value = item[accessor];
        const formatted = formatMoney(Number(value));
        if (value === 0) {
            return <span className="text-gray-400">{formatted}</span>;
        }
        return <span className="text-nowrap">{formatted}</span>;
    };

    return { amountRender, currencies, currency, baseCurrency, formatMoney };
}
